/**
 * The employee page's Tilgang tab (owner brief, 2026-09-21): which companies, which role in each
 * (Bedriftstilgang, unchanged and saving on its own), which teams, and what the role here allows.
 * Rights come from the role, never per person (owner, round 2: read-only, change the role), so
 * the list is the company's own permission vocabulary with a tick where the role has it.
 */
import { type ApiUserFull, type UserWrite, useRoles } from '@/api/data';
import { T } from '@/i18n';
import { Chip, Panel } from '@/ui';
import { Icon } from '@/ui/icons';
import { Link } from 'react-router-dom';
import { Bedriftstilgang } from './Bedriftstilgang';

export function TilgangTab({
  u,
  roleKey,
  roleText,
  body,
  onEditTeams,
}: {
  u: ApiUserFull;
  roleKey: string | null;
  roleText: string;
  body: (c: UserWrite) => UserWrite;
  onEditTeams: () => void;
}) {
  const rolesQ = useRoles();
  const role = rolesQ.data?.roles.find((r) => r.key === roleKey);
  const perms = rolesQ.data?.permissions ?? [];
  // the protected administrator holds every permission whatever its row says
  const has = (k: string) => roleKey === 'admin' || !!role?.permissions.includes(k);
  const teams = (u.teams ?? []).map((t) => t.name).filter(Boolean);
  const granted = perms.filter((p) => has(p.key));
  const not = perms.filter((p) => !has(p.key));
  return (
    <>
      <div id="bedriftstilgang">
        <Bedriftstilgang key={u.id} user={u} body={body} />
      </div>
      <div className="cols2 tgrow">
        <Panel
          item
          title={T('Team')}
          actions={
            <button type="button" className="btn sm" onClick={onEditTeams}>
              <Icon name="sliders" />
              {T('Rediger')}
            </button>
          }
        >
          {teams.length ? (
            <div className="fteams">
              {teams.map((t) => (
                <Chip key={t} tone="neu">
                  {t}
                </Chip>
              ))}
            </div>
          ) : (
            <p className="ovnone">{T('Ikke med i noe team i denne bedriften.')}</p>
          )}
        </Panel>
        <Panel item title={T('Rettigheter')} sub={roleKey ? T('fra rollen {r}', { r: roleText }) : undefined}>
          {!roleKey ? (
            <p className="ovnone">{T('Ingen tilgang til denne bedriften.')}</p>
          ) : (
            <>
              {granted.length ? (
                <ul className="tgperm">
                  {granted.map((p) => (
                    <li key={p.key}>
                      <Icon name="check" />
                      {p.name}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="tgnone">{T('Rollen gir ingen rettigheter utover det alle har.')}</p>
              )}
              {rolesQ.data?.baseline && <p className="hint">{rolesQ.data.baseline}</p>}
              {not.length > 0 && granted.length > 0 && (
                <p className="hint">
                  {T('Ikke med i rollen')}: {not.map((p) => p.name.toLowerCase()).join(', ')}.
                </p>
              )}
              <Link className="ovmore" to="/innstillinger/roller">
                {T('Endre hva rollen gir: Innstillinger → Roller')} <Icon name="chevronRight" />
              </Link>
            </>
          )}
        </Panel>
      </div>
    </>
  );
}
